import React, { useState, useRef, useCallback } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from 'axios';
import styled from 'styled-components';
import "../../../public/assets/css/ontario-design-system/styles/components/tables/tables.css";
import {
  ReportData,
  ReportError,
  ValidationState,
  loadValidationState,
  updateValidationState,
  clearValidationState,
  VALIDATION_STATE_KEY
} from "../../utilities/ValidationStateUtils";
import SessionStorageService from "../../services/SessionStorageService";
import {ReactComponent as UploadIcon} from '../../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-upload.svg';
import {ReactComponent as CloseIcon} from '../../../public/assets/css/ontario-design-system/icons/ontario-icon-close.svg';

// Re-export interfaces for backward compatibility
export type { ReportData, ReportError };

// Styled components for Ontario Design System file upload
const UploadButton = styled.button`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
    border: 1px dashed #1a1a1a;
    border-radius: 3px;
    background-color: #f2f2f2;
    padding: 1rem;
    cursor: pointer;

    &:hover {
      background-color: #e5e5e5;
    }
`;

const FileListContainer = styled.div`
    margin-top: 2rem;
`;

const FileItem = styled.div<{ $hasError?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${props => props.$hasError ? '#fceff0' : '#E2F0F4'};
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    margin-bottom: 0.5rem;
    max-width: 500px;

    &:hover {
      background: ${props => props.$hasError ? '#fae1e3' : '#C2E0FF'};
    }
`;

const FileInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    word-break: break-word;
`;

const FileActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;

    &:hover {
      opacity: 0.7;
    }
`;

const SuccessIcon = () => (
    <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '24px', height: '24px', flexShrink: 0 }}>
        <use href="#ontario-icon-alert-success"></use>
    </svg>
);

const ErrorIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: #d8292f;
    color: white;
    font-weight: bold;
    font-size: 16px;
    flex-shrink: 0;
`;

const LoadingIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <style>
            {`
                @keyframes spinner {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .spinner { animation: spinner 1s linear infinite; transform-origin: center; }
            `}
        </style>
        <circle className="spinner" cx="12" cy="12" r="10" fill="none" stroke="#0066cc" strokeWidth="3" strokeDasharray="31.4 31.4" strokeDashoffset="10" strokeLinecap="round"/>
    </svg>
);

interface UploadedFile {
  file: File;
  attachment: string;
  path: string;
  size: number;
  hasError?: boolean;
  error?: string;
  isLoading?: boolean;
}

const normalizeErrorText = (errors: unknown): string => {
  if (typeof errors === 'string') {
    return errors.trim();
  }

  if (!Array.isArray(errors)) {
    return '';
  }

  return errors
    .map((entry: any) => {
      if (typeof entry === 'string') {
        return entry.trim();
      }

      if (entry && typeof entry === 'object') {
        const field = String(entry.fieldName ?? 'Validation').trim();
        const message = String(entry.error ?? entry.message ?? '').trim();
        return message ? `${field}: ${message}` : field;
      }

      return '';
    })
    .filter((text: string) => text.length > 0)
    .join('; ');
};

const hasReportErrors = (report: any): boolean => normalizeErrorText(report?.errors).length > 0;

const normalizeReportItem = (item: any): ReportData => ({
  nameOfReport: String(item?.nameOfReport ?? ''),
  callNumber: String(item?.callNumber ?? ''),
  errors: normalizeErrorText(item?.errors),
  status: String(item?.status ?? '')
});

const normalizeReportList = (items: unknown): ReportData[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map(normalizeReportItem);
};

const splitErrorParts = (errorText: string): string[] => {
  const withLineBreaks = errorText.replace(/\.\s+([A-Za-z][A-Za-z0-9]*:)/g, '.\n$1');
  return withLineBreaks
    .split(/;\s+|\n/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

const combineUrl = (base?: string, path?: string): string => {
  if (!path) return base || '';
  if (!base) return path;
  // If path already starts with http/https, just return it
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  // Ensure only one slash between base and path
  return base.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
};

interface Props {
  reports: ReportData[];
  isEng: boolean;
  onRemove?: (report: ReportData) => void;
  readOnly?: boolean;
  // New props for enhanced functionality
  allowUpload?: boolean;
  baseUrl?: string;
  apiUrl?: string;
  apiMethod?: 'POST' | 'PUT';
  onNavigate?: (sectionIndex: number) => void;
  onFilesChange?: (files: UploadedFile[]) => void;
  acceptFileType?: string;
  validateXML?: boolean;
  maxFileSize?: number; // in MB
  authRequired?: boolean;
  // Configurable rich text content for upload section
  uploadSectionContentEN?: string;
  uploadSectionContentFR?: string;
}

export const ReportDataTable: React.FC<Props> = ({
  reports,
  isEng,
  onRemove,
  readOnly = false,
  allowUpload = false,
  baseUrl,
  apiUrl,
  apiMethod = 'PUT',
  onNavigate,
  onFilesChange,
  acceptFileType = '.xml',
  validateXML = true,
  maxFileSize = 20,
  authRequired = false,
  uploadSectionContentEN,
  uploadSectionContentFR
}) => {
  // State
  const [tableData, setTableData] = useState<ReportData[]>(() => getInitialData());
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);

  /**
   * Get initial data from sessionStorage
   */
  function getInitialData(): ReportData[] {
    const state = loadValidationState();
    if (state?.data && state.data.length > 0) {
      return normalizeReportList(state.data);
    }

    const legacyKey = readOnly ? 'finalReviewData' : 'lastApiResponse';
    const legacyData = sessionStorage.getItem(legacyKey);
    if (legacyData) {
      try {
        const parsed = JSON.parse(legacyData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return normalizeReportList(parsed);
        }
      } catch (e) {
        console.error('ReportDataTable: Failed to parse session data', e);
      }
    }

    return normalizeReportList(reports || []);
  }

  /**
   * Validate XML file structure
   */
  const validateXMLFile = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    if (!validateXML) {
      return { valid: true };
    }

    const isXMLFile = file.name.toLowerCase().endsWith('.xml');
    if (!isXMLFile) {
      return { valid: true };
    }

    try {
      const text = await file.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');

      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        return {
          valid: false,
          error: isEng ? 'Invalid XML structure' : 'Structure XML invalide'
        };
      }

      return { valid: true };
    } catch (e) {
      return {
        valid: false,
        error: isEng ? 'Failed to parse XML file' : 'Impossible d\'analyser le fichier XML'
      };
    }
  };

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  /**
   * Handle file upload
   */
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadedFile[] = [];
    const maxSizeBytes = maxFileSize * 1024 * 1024;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check file size
      if (file.size > maxSizeBytes) {
        newFiles.push({
          file,
          attachment: '',
          path: file.name,
          size: file.size,
          hasError: true,
          error: isEng
            ? `File size exceeds ${maxFileSize}MB limit`
            : `La taille du fichier dépasse la limite de ${maxFileSize}Mo`,
          isLoading: false
        });
        continue;
      }

      // Validate XML
      const validation = await validateXMLFile(file);
      if (!validation.valid) {
        newFiles.push({
          file,
          attachment: '',
          path: file.name,
          size: file.size,
          hasError: true,
          error: validation.error,
          isLoading: false
        });
        continue;
      }

      // Convert to base64
      try {
        const base64 = await fileToBase64(file);
        newFiles.push({
          file,
          attachment: base64,
          path: file.name,
          size: file.size,
          hasError: false,
          isLoading: false
        });
      } catch (e) {
        newFiles.push({
          file,
          attachment: '',
          path: file.name,
          size: file.size,
          hasError: true,
          error: isEng ? 'Failed to read file' : 'Impossible de lire le fichier',
          isLoading: false
        });
      }
    }

    // Overwrite files with same name (latest one wins)
    setUploadedFiles(prev => {
      const updatedFiles = [...prev];
      newFiles.forEach(newFile => {
        const existingIndex = updatedFiles.findIndex(f => f.path === newFile.path);
        if (existingIndex >= 0) {
          // Replace existing file with same name
          updatedFiles[existingIndex] = newFile;
        } else {
          // Add new file
          updatedFiles.push(newFile);
        }
      });
      return updatedFiles;
    });

    // Also update callback with merged files
    const mergedFiles = [...uploadedFiles];
    newFiles.forEach(newFile => {
      const existingIndex = mergedFiles.findIndex(f => f.path === newFile.path);
      if (existingIndex >= 0) {
        mergedFiles[existingIndex] = newFile;
      } else {
        mergedFiles.push(newFile);
      }
    });
    onFilesChange?.(mergedFiles);

    // Clear input
    if (uploadRef.current) {
      uploadRef.current.value = '';
    }
  };

  /**
   * Remove uploaded file
   */
  const handleRemoveUploadedFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    onFilesChange?.(newFiles);
  };

  /**
   * Remove all files with errors
   */
  const handleRemoveFailedFiles = () => {
    const validFiles = uploadedFiles.filter(f => !f.hasError);
    setUploadedFiles(validFiles);
    onFilesChange?.(validFiles);
    setShowErrorDialog(false);
  };

  /**
   * Handle remove from table
   */
  const handleRemove = (report: ReportData) => {
    const updatedData = tableData.filter(r => r !== report);
    setTableData(updatedData);
    updateValidationState(updatedData);

    // Legacy keys for backward compatibility
    sessionStorage.setItem('lastApiResponse', JSON.stringify(updatedData));
    const hasErrors = updatedData.some(r => hasReportErrors(r));
    sessionStorage.setItem('validationHasErrors', hasErrors ? 'true' : 'false');
    if (!hasErrors) {
      sessionStorage.setItem('finalReviewData', JSON.stringify(updatedData));
    }

    onRemove?.(report);
  };

  /**
   * Determine button mode
   */
  const hasNewFiles = uploadedFiles.length > 0;
  const hasLocalErrors = uploadedFiles.some(f => f.hasError);
  const hasTableErrors = tableData.some(r => hasReportErrors(r));
  const isValidateMode = hasNewFiles || hasTableErrors;

  /**
   * Handle Validate/Continue button click
   */
  const handleButtonClick = async () => {
    // Check for local validation errors
    if (hasLocalErrors) {
      setShowErrorDialog(true);
      return;
    }

    // CONTINUE MODE - no new files and no errors
    if (!isValidateMode) {
      // Store final data and navigate
      sessionStorage.setItem('finalReviewData', JSON.stringify(tableData));
      onNavigate?.(2); // Navigate to Section 3
      return;
    }

    // VALIDATE MODE - call API with new files
    if (!apiUrl) {
      console.error('ReportDataTable: No API URL configured');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare payload - only new files
      const validFiles = uploadedFiles.filter(f => !f.hasError);
      const payload = {
        files: validFiles.map(f => ({
          attachment: f.attachment,
          path: f.path,
          size: f.size
        }))
      };

      // Get auth token if required (authRequired can be 'ps', 'bps', etc.)
      const token = authRequired ? SessionStorageService.getAuthToken(String(authRequired)) : null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Make API call
      const fullUrl = combineUrl(baseUrl, apiUrl);
      const response = await axios({
        method: apiMethod,
        url: fullUrl,
        data: payload,
        headers
      });

      // Process response
      if (response.data && Array.isArray(response.data)) {
        // Normalize new results
        const newResults: ReportData[] = normalizeReportList(response.data);

        // MERGE with existing data - overwrite reports with same name (latest wins)
        const mergedData = [...tableData];
        newResults.forEach(newResult => {
          const existingIndex = mergedData.findIndex(r => r.nameOfReport === newResult.nameOfReport);
          if (existingIndex >= 0) {
            // Replace existing report with same name
            mergedData[existingIndex] = newResult;
          } else {
            // Add new report
            mergedData.push(newResult);
          }
        });

        // Update state
        setTableData(mergedData);
        updateValidationState(mergedData);

        // Legacy keys
        sessionStorage.setItem('lastApiResponse', JSON.stringify(mergedData));
        const hasErrors = mergedData.some(r => hasReportErrors(r));
        sessionStorage.setItem('validationHasErrors', hasErrors ? 'true' : 'false');

        if (!hasErrors) {
          sessionStorage.setItem('finalReviewData', JSON.stringify(mergedData));
        }
        // After validation, stay on current page - user clicks "Continue" to proceed

        // Clear uploaded files after successful validation
        setUploadedFiles([]);
        onFilesChange?.([]);
      }
    } catch (error) {
      console.error('ReportDataTable: API call failed', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Template functions
  const errorsBodyTemplate = (rowData: ReportData) => {
    const errorText = normalizeErrorText((rowData as any).errors);
    if (!errorText) {
      return <span style={{ color: '#6e6e6e' }}>N/A</span>;
    }
    const parts = splitErrorParts(errorText);
    if (parts.length === 1) {
      return <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{parts[0]}</span>;
    }
    return (
      <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
        {parts.map((part, i) => (
          <li key={i} style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{part}</li>
        ))}
      </ul>
    );
  };

  const actionBodyTemplate = (rowData: ReportData) => {
    return (
      <button
        style={{
          background: 'none',
          border: 'none',
          color: '#0066CC',
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: 0,
          font: 'inherit'
        }}
        onClick={() => handleRemove(rowData)}
        aria-label={`${isEng ? 'Remove' : 'Retirer'} ${rowData.nameOfReport}`}
      >
        {isEng ? 'Remove' : 'Retirer'}
      </button>
    );
  };

  const statusBodyTemplate = (rowData: ReportData) => {
    const status = String((rowData as any).status ?? '').trim().toLowerCase();
    if (status.includes('fail')) {
      return <span>{isEng ? 'Failure' : 'Échec'}</span>;
    }
    if (status.includes('success')) {
      return <span>{isEng ? 'Success' : 'Succès'}</span>;
    }

    return hasReportErrors(rowData)
      ? <span>{isEng ? 'Failure' : 'Échec'}</span>
      : <span>{isEng ? 'Success' : 'Succès'}</span>;
  };

  const rowClassName = (data: ReportData) => {
    return hasReportErrors(data) ? 'row-has-errors' : '';
  };

  // Effects
  React.useEffect(() => {
    if (reports && reports.length > 0) {
      setTableData(normalizeReportList(reports));
    }
  }, [reports]);

  React.useEffect(() => {
    const handleTableUpdate = (event: CustomEvent) => {
      if (event.detail && Array.isArray(event.detail)) {
        setTableData(normalizeReportList(event.detail));
      }
    };

    const handleStorageUpdate = () => {
      const state = loadValidationState();
      if (state?.data && state.data.length > 0) {
        setTableData(normalizeReportList(state.data));
        return;
      }

      if (readOnly) {
        const finalData = sessionStorage.getItem('finalReviewData');
        if (finalData) {
          try {
            const parsedFinal = JSON.parse(finalData);
            setTableData(normalizeReportList(parsedFinal));
            return;
          } catch (e) {
            console.error('ReportDataTable: Failed to parse finalReviewData', e);
          }
        }
      }

      const storedData = sessionStorage.getItem('lastApiResponse');
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          setTableData(normalizeReportList(parsed));
        } catch (e) {
          console.error('ReportDataTable: Failed to parse updated data', e);
        }
      }
    };

    window.addEventListener('reportTableUpdate', handleTableUpdate as EventListener);
    window.addEventListener('storage', handleStorageUpdate);
    window.addEventListener('refreshReportTable', handleStorageUpdate);

    return () => {
      window.removeEventListener('reportTableUpdate', handleTableUpdate as EventListener);
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener('refreshReportTable', handleStorageUpdate);
    };
  }, []);

  // Accessibility fix for table
  React.useEffect(() => {
    const dataTable = document.querySelector('.report-data-table');
    if (dataTable) {
      const table = dataTable.getElementsByTagName('table')?.[0];
      if (table) {
        table.removeAttribute('role');
        table.querySelector('thead')?.removeAttribute('role');
        table.querySelector('tbody')?.removeAttribute('role');
        table.querySelectorAll('thead tr')?.forEach((element) => element.removeAttribute('role'));
        table.querySelectorAll('tbody tr')?.forEach((element) => element.removeAttribute('role'));
        table.querySelectorAll('tbody td')?.forEach((element) => element.removeAttribute('role'));
        table.querySelectorAll('thead th')?.forEach((element) => {
          element.removeAttribute('role');
          element.setAttribute('scope', 'col');
        });
      }
    }
  }, [tableData]);

  const errorCount = tableData.filter(r => hasReportErrors(r)).length;
  const totalCount = tableData.length;
  const hasErrors = errorCount > 0;

  const getErrorMessage = () => {
    if (isEng) {
      return `${errorCount} of ${totalCount} incident report${totalCount > 1 ? 's' : ''} had an error`;
    } else {
      return `${errorCount} rapport${errorCount > 1 ? 's' : ''} d'incident sur ${totalCount} comportai${errorCount > 1 ? 'ent' : 't'} une erreur`;
    }
  };

  const getSuccessMessage = () => {
    if (isEng) {
      return `${totalCount} file${totalCount > 1 ? 's are' : ' is'} now ready for review and submission`;
    } else {
      return `${totalCount} fichier${totalCount > 1 ? 's sont' : ' est'} maintenant prêt${totalCount > 1 ? 's' : ''} pour examen et soumission`;
    }
  };

  return (
    <div>
      {/* Error Callout */}
      {!readOnly && hasErrors && totalCount > 0 && (
        <div className="ontario-alert ontario-alert--error" style={{ marginBottom: '2rem' }}>
          <div className="ontario-alert__header">
            <div className="ontario-alert__header-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h2 className="ontario-alert__header-title ontario-h4">
              {getErrorMessage()}
            </h2>
          </div>
        </div>
      )}

      {/* Success Callout */}
      {!readOnly && !hasErrors && totalCount > 0 && (
        <div className="ontario-alert ontario-alert--success" style={{ marginBottom: '2rem' }}>
          <div className="ontario-alert__header">
            <div className="ontario-alert__header-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h2 className="ontario-alert__header-title ontario-h4">
              {getSuccessMessage()}
            </h2>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="ontario-table-container ontario-small-12 ontario-medium-12 ontario-large-12">
        <div className="ontario-table-div">
          <style>{`
            .report-data-table .row-has-errors {
              background-color: #fceff0 !important;
            }
            .report-data-table .row-has-errors:hover {
              background-color: #fae1e3 !important;
            }
            .report-data-table .p-datatable-tbody > tr > td {
              vertical-align: top;
              word-break: break-word;
              white-space: normal;
            }
          `}</style>
          <DataTable
            value={tableData}
            className="report-data-table"
            stripedRows
            sortMode="multiple"
            rowClassName={rowClassName}
          >
            <Column
              field="nameOfReport"
              header={isEng ? "Name of report" : "Nom du rapport"}
              sortable
            />
            <Column
              field="callNumber"
              header={isEng ? "Call number" : "Numéro d'appel"}
              sortable
            />
            <Column
              header={isEng ? "Errors" : "Erreurs"}
              body={errorsBodyTemplate}
              style={{ minWidth: '20rem' }}
            />
            {readOnly ? (
              <Column
                header={isEng ? "Status" : "Statut"}
                body={statusBodyTemplate}
                sortable
              />
            ) : (
              <Column
                header={isEng ? "Action" : "Action"}
                body={actionBodyTemplate}
              />
            )}
          </DataTable>
        </div>
      </div>

      {/* File Upload Section - Ontario Design System Style */}
      {allowUpload && !readOnly && (
        <div style={{ marginTop: '2rem' }}>
          {/* Custom Rich Text Content - Only show if configured */}
          {(isEng ? uploadSectionContentEN : uploadSectionContentFR) && (
            <div
              dangerouslySetInnerHTML={{
                __html: isEng ? uploadSectionContentEN! : uploadSectionContentFR!
              }}
            />
          )}

          {/* Upload Button - Ontario Design System Style */}
          <UploadButton
            type="button"
            onClick={() => uploadRef.current?.click()}
          >
            <div style={{ display: 'flex' }}>
              <div style={{ marginRight: '0.5rem' }}>
                <UploadIcon fill='#0066CC' style={{ width: '24px', height: '24px' }} />
              </div>
              <div>
                <p style={{ textAlign: 'left', fontSize: '20px', fontWeight: '700', marginBottom: '0.5rem', color: '#0066CC', margin: 0 }}>
                  <strong>{isEng ? 'Upload a file' : 'Télécharger un fichier'}</strong>
                </p>
                <p style={{ textAlign: 'left', fontSize: '16px', marginBottom: 0, color: '#1a1a1a', marginTop: '0.25rem' }}>
                  {isEng
                    ? `XML, max size ${maxFileSize}MB`
                    : `XML, taille maximale ${maxFileSize}Mo`
                  }
                </p>
              </div>
            </div>
          </UploadButton>
          <input
            ref={uploadRef}
            type="file"
            accept={acceptFileType}
            multiple
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="report-file-upload"
          />

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <FileListContainer>
              <h4 className="ontario-h5" style={{ marginBottom: '1rem' }}>
                {isEng ? 'Uploaded files' : 'Fichiers téléchargés'}
              </h4>
              {uploadedFiles.map((file, index) => (
                <FileItem key={index} $hasError={file.hasError}>
                  <FileInfo>
                    {file.isLoading ? (
                      <LoadingIcon />
                    ) : file.hasError ? (
                      <ErrorIcon>!</ErrorIcon>
                    ) : (
                      <SuccessIcon />
                    )}
                    <span>{file.path}</span>
                  </FileInfo>
                  <FileActions>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      {(file.size / 1024).toFixed(2)}KB
                    </span>
                    <CloseButton
                      onClick={() => handleRemoveUploadedFile(index)}
                      aria-label={isEng ? 'Remove file' : 'Supprimer le fichier'}
                    >
                      <CloseIcon style={{ width: '24px', height: '24px' }} />
                    </CloseButton>
                  </FileActions>
                </FileItem>
              ))}
              <p style={{ marginTop: '0.5rem', color: '#666' }}>
                {isEng
                  ? `You have uploaded ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}.`
                  : `Vous avez téléchargé ${uploadedFiles.length} fichier${uploadedFiles.length > 1 ? 's' : ''}.`
                }
              </p>
              {hasLocalErrors && (
                <p style={{ color: '#d8292f', marginTop: '0.5rem' }}>
                  {isEng
                    ? 'Some files have errors. Please remove them before validating.'
                    : 'Certains fichiers contiennent des erreurs. Veuillez les supprimer avant de valider.'
                  }
                </p>
              )}
            </FileListContainer>
          )}
        </div>
      )}

      {/* Validate/Continue Button - Only show if not readOnly */}
      {!readOnly && (
        <div style={{ marginTop: '2rem' }}>
          <button
            className="ontario-button ontario-button--primary"
            onClick={handleButtonClick}
            disabled={isLoading}
          >
            {isLoading ? (
              isEng ? 'Loading...' : 'Chargement...'
            ) : isValidateMode ? (
              isEng ? 'Validate' : 'Valider'
            ) : (
              isEng ? 'Continue' : 'Continuer'
            )}
          </button>
        </div>
      )}

      {/* Error Dialog */}
      {showErrorDialog && (
        <div
          className="ontario-file-error-dialog-overlay"
          onClick={() => setShowErrorDialog(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="file-error-dialog-title"
        >
          <div className="ontario-file-error-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              className="ontario-file-error-dialog__close"
              onClick={() => setShowErrorDialog(false)}
              aria-label={isEng ? 'Close dialog' : 'Fermer la boîte de dialogue'}
              type="button"
            >
              ×
            </button>
            <h4 className="ontario-file-error-dialog__heading" id="file-error-dialog-title">
              {isEng ? 'File error detected' : 'Erreur de fichier détectée'}
            </h4>
            <p className="ontario-file-error-dialog__body">
              {isEng
                ? 'The uploaded file contains an error and cannot be submitted. Please remove the file and/or upload a valid version to continue.'
                : 'Le fichier téléchargé contient une erreur et ne peut pas être soumis. Veuillez supprimer le fichier et/ou télécharger une version valide pour continuer.'}
            </p>
            <div className="ontario-file-error-dialog__actions">
              <button
                className="ontario-button ontario-button--primary"
                onClick={handleRemoveFailedFiles}
                type="button"
              >
                {isEng ? 'Remove file' : 'Supprimer le fichier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};